/** UXP-00010 — Customer Operations Platform framework (mirrors pkg/customerops) */

export const UXP_CO_OPS_ID = 'UXP-00010';

export interface CommandCenterKPI {
  label: string;
  value: string;
  change: string;
  positive: boolean;
}

export interface OrderIntelRow {
  dimension: string;
  value: string;
  trend: string;
  positive: boolean;
}

export interface OrderRow {
  id: string;
  customer: string;
  items: number;
  value: string;
  status: string;
  sla: string;
}

export interface CustomerRow {
  name: string;
  ltv: string;
  repeatScore: number;
  loyaltyScore: number;
  orders: number;
}

export interface FulfillmentRow {
  order: string;
  warehouse: string;
  carrier: string;
  priority: string;
}

export interface ReturnRefundRow {
  id: string;
  type: string;
  reason: string;
  value: string;
  status: string;
}

export interface RetentionRow {
  type: string;
  title: string;
  customers: number;
  impact: string;
  confidence: number;
}

export interface AdvisorRec {
  type: string;
  title: string;
  impact: string;
  confidence: number;
}

export const COMMAND_CENTER_METRICS = [
  'Order Health', 'Operational Health', 'Pending Orders', 'Shipment Status',
  'Returns', 'Refunds', 'Customer Satisfaction', 'Operational KPIs',
];

export const ORDER_INTEL_DIMENSIONS = [
  'Order Velocity', 'Revenue', 'Profit', 'Customer Segments', 'Marketplace Mix',
  'Category Mix', 'Risk', 'Fraud', 'Delivery Success',
];

export const ORDER_WORKSPACE_FEATURES = [
  'Order Timeline', 'Customer', 'Products', 'Payments', 'Invoices',
  'Shipments', 'Returns', 'Refunds', 'Messages', 'Audit Trail',
];

export const CUSTOMER_INTEL_FEATURES = [
  'Customer Profiles', 'Purchase History', 'Lifetime Value', 'Repeat Purchase Score',
  'Loyalty Score', 'Support History', 'Review History', 'Customer Intent DNA',
];

export const FULFILLMENT_FEATURES = [
  'Warehouse Allocation', 'Carrier Selection', 'Shipment Planning', 'Split Shipments',
  'Combined Shipments', 'Marketplace Fulfillment', 'Merchant Fulfillment',
];

export const SHIPPING_FEATURES = [
  'Carrier Selection', 'Transit Time', 'Delivery Success', 'Shipping Cost', 'Delay Prediction', 'Tracking',
];

export const RETURNS_REFUNDS_FEATURES = [
  'Return Requests', 'Refund Requests', 'Reason Analysis', 'Fraud Detection',
  'Approval Workflows', 'Marketplace Integration',
];

export const COMMUNICATION_FEATURES = [
  'Marketplace Messages', 'Email', 'Notifications', 'AI-generated Replies',
  'Sentiment Analysis', 'Conversation History',
];

export const REVIEW_INTEL_FEATURES = [
  'Ratings', 'Review Trends', 'Negative Reviews', 'Positive Reviews',
  'Keyword Extraction', 'Sentiment', 'AI Response Suggestions',
];

export const RETENTION_PREDICTIONS = [
  'Repeat Purchases', 'Cross-sell Opportunities', 'Upsell Opportunities',
  'Churn Risk', 'Loyalty Opportunities', 'Win-back Campaigns',
];

export const ADVISOR_RECOMMENDATIONS = [
  'Shipment Priorities', 'Refund Decisions', 'Customer Communication',
  'Review Requests', 'Retention Campaigns', 'Operational Improvements',
];

export const SLA_METRICS = [
  'Packing', 'Shipping', 'Delivery', 'Customer Response', 'Returns', 'Refunds', 'Marketplace Commitments',
];

export const COLLABORATION_FEATURES = [
  'Assignments', 'Mentions', 'Escalations', 'Comments', 'Approvals', 'Decision History',
];

export const ANALYTICS_METRICS = [
  'Order Processing Time', 'On-time Delivery', 'Return Rate', 'Refund Rate',
  'Customer Satisfaction', 'LTV', 'Operational Efficiency',
];

export const AI_LEARNING_SOURCES = [
  'Order Outcomes', 'Delivery Outcomes', 'Customer Feedback', 'Returns', 'Refunds',
  'Reviews', 'Operational Decisions', 'Customer Intent DNA',
];

export const ACCESSIBILITY_STANDARDS = [
  'WCAG 2.2 AA', 'Keyboard Navigation', 'Screen Reader Support', 'Reduced Motion', 'Responsive Design',
];

export const ARCHITECTURE_STAGES = [
  'Fulfillment Command Center', 'Order Intelligence', 'Order Workspace', 'Customer Intelligence',
  'Fulfillment Operations', 'Shipping & Delivery', 'Returns & Refunds', 'Customer Communication',
  'Review Intelligence', 'Retention Engine', 'AI Customer Ops Advisor', 'SLA Management',
];

export const LIVE_ROUTES: Record<string, string> = {
  orderOps: '/amazon/order-ops',
  postSalesOps: '/amazon/post-sales-ops',
  orders: '/amazon/orders',
};

export const DEMO_KPIS: CommandCenterKPI[] = [
  { label: 'Order Health', value: '94%', change: '+2pp', positive: true },
  { label: 'Operational Health', value: '91%', change: '+1pp', positive: true },
  { label: 'Pending Orders', value: '342', change: '-8%', positive: true },
  { label: 'In Transit', value: '1,248', change: '+12%', positive: true },
  { label: 'Returns (7d)', value: '48', change: '-5%', positive: true },
  { label: 'Refunds (7d)', value: '32', change: '-3%', positive: true },
  { label: 'CSAT', value: '4.6/5', change: '+0.2', positive: true },
  { label: 'On-time Delivery', value: '96.2%', change: '+1.1pp', positive: true },
];

export const DEMO_ORDER_INTEL: OrderIntelRow[] = [
  { dimension: 'Order Velocity', value: '842/day', trend: '+14%', positive: true },
  { dimension: 'Revenue', value: '₹18.4L/day', trend: '+11%', positive: true },
  { dimension: 'Profit', value: '₹5.2L/day', trend: '+9%', positive: true },
  { dimension: 'Delivery Success', value: '96.2%', trend: '+1.1pp', positive: true },
  { dimension: 'Fraud Flags', value: '3', trend: '-40%', positive: true },
];

export const DEMO_ORDERS: OrderRow[] = [
  { id: 'ORD-2026-18420', customer: 'Priya S.', items: 2, value: '₹4,299', status: 'packing', sla: '2h left' },
  { id: 'ORD-2026-18419', customer: 'Rahul M.', items: 1, value: '₹2,499', status: 'shipped', sla: 'on track' },
  { id: 'ORD-2026-18418', customer: 'Ananya K.', items: 3, value: '₹8,940', status: 'delivered', sla: 'completed' },
  { id: 'ORD-2026-18417', customer: 'Vikram P.', items: 1, value: '₹1,899', status: 'return_requested', sla: 'review pending' },
];

export const DEMO_CUSTOMERS: CustomerRow[] = [
  { name: 'Priya S.', ltv: '₹42,800', repeatScore: 87, loyaltyScore: 92, orders: 8 },
  { name: 'Rahul M.', ltv: '₹28,400', repeatScore: 72, loyaltyScore: 78, orders: 5 },
  { name: 'Ananya K.', ltv: '₹64,200', repeatScore: 94, loyaltyScore: 96, orders: 12 },
];

export const DEMO_FULFILLMENT: FulfillmentRow[] = [
  { order: 'ORD-2026-18420', warehouse: 'Mumbai WH-01', carrier: 'BlueDart', priority: 'high' },
  { order: 'ORD-2026-18416', warehouse: 'Delhi WH-02', carrier: 'Delhivery', priority: 'standard' },
  { order: 'ORD-2026-18415', warehouse: 'Bangalore WH-03', carrier: 'Amazon MFN', priority: 'express' },
];

export const DEMO_RETURNS: ReturnRefundRow[] = [
  { id: 'RET-2026-0421', type: 'Return', reason: 'Wrong size', value: '₹1,899', status: 'pending_approval' },
  { id: 'REF-2026-0318', type: 'Refund', reason: 'Damaged in transit', value: '₹2,499', status: 'approved' },
  { id: 'RET-2026-0420', type: 'Return', reason: 'Changed mind', value: '₹899', status: 'received' },
];

export const DEMO_RETENTION: RetentionRow[] = [
  { type: 'Repeat Purchases', title: 'Earbuds Pro repeat buyers — 340 customers', customers: 340, impact: '+₹8.2L LTV', confidence: 88 },
  { type: 'Cross-sell', title: 'Hub Gen 3 cross-sell to Hub Gen 2 owners', customers: 128, impact: '+₹4.1L revenue', confidence: 82 },
  { type: 'Churn Risk', title: 'At-risk customers — no purchase in 90 days', customers: 56, impact: 'Prevent ₹2.4L churn', confidence: 79 },
  { type: 'Win-back', title: 'Win-back campaign for lapsed yoga mat buyers', customers: 92, impact: '+18% reactivation', confidence: 74 },
];

export const DEMO_ADVISOR: AdvisorRec[] = [
  { type: 'Shipment Priorities', title: 'Expedite ORD-2026-18420 — Prime member, SLA at risk', impact: 'Protect CSAT + Prime badge', confidence: 94 },
  { type: 'Refund Decisions', title: 'Auto-approve REF-2026-0318 — carrier damage confirmed', impact: 'Save 2h agent time', confidence: 91 },
  { type: 'Customer Communication', title: 'Send proactive delay notice for 12 in-transit BLR8 orders', impact: 'Reduce support tickets 40%', confidence: 86 },
  { type: 'Review Requests', title: 'Request review from 48 delivered orders (4.8+ CSAT)', impact: '+12 reviews/week', confidence: 83 },
  { type: 'Retention Campaigns', title: 'Launch win-back for 56 at-risk customers', impact: '+₹2.4L LTV protected', confidence: 78 },
];

export function buildCustomerOpsDashboard() {
  return {
    command_center_metrics: COMMAND_CENTER_METRICS.length,
    order_intel_dimensions: ORDER_INTEL_DIMENSIONS.length,
    workspace_features: ORDER_WORKSPACE_FEATURES.length,
    fulfillment_features: FULFILLMENT_FEATURES.length,
    returns_features: RETURNS_REFUNDS_FEATURES.length,
    retention_predictions: RETENTION_PREDICTIONS.length,
  };
}
